import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {
  Badge,
  Button,
  Table,
  TableSkeleton,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {BusinessPaymentMethod} from '@/types/AfriexBusinessPaymentMethod';
import type {AfriexBankAccount} from '@/types/AfriexPaymentMethod';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {getAccountType, statusColorScheme} from '@/utils/merchant';

import {useBusinessVirtualAccounts} from './useBusinessVirtualAccounts';
import VirtualAccountSummary from './VirtualAccountSummary';

const VirtualAccountRow = ({
  account,
  index,
}: {
  account: BusinessPaymentMethod;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const info = (account.info ?? {}) as AfriexBankAccount;
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              )}
            </div>
          </Button>
        </Td>
        <Td className="font-medium">{info.accountName || '—'}</Td>
        <Td>{account.currencyCode || info.currency || '—'}</Td>
        <Td>{getAccountType(info)}</Td>
        <Td>
          <Badge
            label={capitalizeWords(account.status)}
            colorScheme={statusColorScheme(account.status)}
          />
        </Td>
        <Td>
          {account.createdAt
            ? formatDate.DDMonYYYY(account.createdAt.toString())
            : '—'}
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0 m-0 whitespace-nowrap">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <VirtualAccountSummary account={account} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

const BusinessVirtualAccountsTab = ({businessId}: {businessId: string}) => {
  const {virtualAccounts, isLoading} = useBusinessVirtualAccounts(businessId);

  if (isLoading) {
    return (
      <div className="px-3 pt-3">
        <TableSkeleton />
      </div>
    );
  }

  if (!virtualAccounts.length) {
    return (
      <div className="px-3 pt-10 pb-10 flex flex-col items-center justify-center text-center">
        <h3 className="font-semibold text-gray-900 dark:text-gray-200 mb-1">
          No virtual accounts
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
          This business has no virtual accounts linked across any provider or
          currency yet.
        </p>
      </div>
    );
  }

  return (
    <div className="px-3 pt-3">
      <Table className="overflow-hidden">
        <Thead>
          <Tr enableHover={false}>
            <Th className="w-20">&nbsp;</Th>
            <Th>Account Name</Th>
            <Th>Currency</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Date Created</Th>
          </Tr>
        </Thead>
        <TBody>
          {virtualAccounts.map((account, index) => (
            <VirtualAccountRow
              key={account.id ?? index}
              account={account}
              index={index}
            />
          ))}
        </TBody>
      </Table>
    </div>
  );
};

export default BusinessVirtualAccountsTab;
